<template>
  <div>
    <!-- Chart container -->
    <div
      ref="chartRef"
      style="width: 1100px; height: 400px; overflow-x: auto;"
    />
  </div>
</template>

<script setup>
import * as echarts from 'echarts'
import { onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  chartData: {
    type: Array,
    required: true,
  },
  chartCategories: {
    type: Array,
    required: true,
  },
  legendLabels: {
    type: Array,
    required: true,
  },
  seriesColors: {
    type: Array,
    required: true,
  },
})

const chartRef = ref(null)
let chartInstance = null

const initChart = () => {
  const chartDom = chartRef.value

  if (chartDom) {
    if (chartInstance) {
      chartInstance.dispose()
    }
    chartInstance = echarts.init(chartDom)

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow', // Improves tooltip readability
        },
      },
      legend: {
        data: props.legendLabels, // Use dynamic legend labels
        top: '5%',
        left: 'center',
        textStyle: {
          fontSize: 12, // Adjust font size for legend labels
        },
      },
      grid: {
        top: '20%',
        left: '5%',
        right: '5%',
        bottom: '20%', // Adjust space for x-axis labels
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: props.chartCategories,
        axisLabel: {
          interval: 0, // Display all labels
          rotate: 0, // Rotate labels for better visibility 
          textStyle: {
            fontSize: 10,
          },
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}', // Ensure numeric formatting
        },
        splitLine: {
          lineStyle: {
            type: 'dashed', // Dashed lines for better readability
          },
        },
      },
      series: props.legendLabels.map((label, index) => ({
        name: label,
        type: 'bar',
        data: props.chartData[index] || [],
        barMaxWidth: 30, // Adjust bar width
        itemStyle: {
          color: props.seriesColors[index], // Use dynamic colors
        },
      })),
    }

    chartInstance.setOption(option)
    window.addEventListener('resize', chartInstance.resize)
  }
}

onMounted(initChart)

onUnmounted(() => {
  if (chartInstance) {
    window.removeEventListener('resize', chartInstance.resize)
    chartInstance.dispose()
  }
})

// Watch for data changes and reinitialize the chart
watch(() => [props.chartData, props.chartCategories, props.legendLabels, props.seriesColors], initChart)
</script>

<style scoped>
/* Optional: Add custom styling */
</style>
